import React, { useState, useEffect } from 'react'; 
import Modal from 'react-bootstrap/Modal';
import { Button, Form, Table, Spinner, Alert } from 'react-bootstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import fetchApi from '../../api/fetchApi';
import ModalAlert from '../template/ModalAlert';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';
import './UserList.css';

interface ModalProjectPersonProps {
    isOpen: boolean;
    onClose: () => void;
    projectId?: number;
    personEmail?: string;
    personId: number;
}

const validationSchema = Yup.object({
    projectId: Yup.number().notOneOf([0], 'Este campo es requerido').required('Este campo es requerido'),  
    status: Yup.string().notOneOf(['','null'], 'Este campo es requerido').required('Este campo es requerido'),  
});

const ModalProjectPerson: React.FC<ModalProjectPersonProps> = ({ isOpen, onClose, projectId, personId, personEmail }) => {
    const [modalAlertOpen, setModalAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<'success' | 'error'>('success');
    const [projects, setProjects] = useState<any[]>([]);
    const [personProjects, setPersonProjects] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({ page: 1, size: 4, numberPages: 1 });
    
    const formik = useFormik({
        initialValues: {
            personId: personId || 0,  
            projectId: projectId || 0,
            status: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                await fetchApi({
                    endpoint: '/project-person',
                    method: 'POST',
                    body: { ...values, personId },
                });
                setAlertMessage('Persona asignada correctamente.');
                setAlertType('success');
                setModalAlertOpen(true);
                onClose();
            } catch (error) {
                setAlertMessage('Error al asignar la persona al proyecto.');
                setAlertType('error');
                setModalAlertOpen(true);
            }
        },
    });

    useEffect(() => {
        if (!isOpen) {
            setModalAlertOpen(false);
        } else {
            formik.resetForm();
        }
    }, [isOpen]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await fetchApi({ endpoint: '/projects', method: 'GET' });
                setProjects(response);  
            } catch (error) {
                setAlertMessage('Error al cargar los proyectos.');
                setAlertType('error');
                setModalAlertOpen(false);
            }
        };
        fetchProjects();
    }, []);

    useEffect(() => {
        const fetchPersonProjects = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetchApi({ endpoint: `/project-person/person/${personId}`, method: 'GET' });
                setPersonProjects(response);
                setPagination({ page: 1, size: 4, numberPages: Math.ceil(response.length / 4) });
            } catch (error) {
                setError('Error al cargar los proyectos de la persona.');
            } finally {
                setLoading(false);
            }
        };
    
        if (isOpen && personId) {
            fetchPersonProjects();
        }
    }, [isOpen, personId]);

    const handlePaginationChange = (page: number) => {
        setPagination((prev) => ({ ...prev, page }));
    };

    const displayedData = personProjects.slice((pagination.page - 1) * pagination.size, pagination.page * pagination.size);

    return (
        <Modal show={isOpen} onHide={onClose} size="xl" fullscreen="lg-down">
            <Modal.Header closeButton className="modal-header-custom">
                <Modal.Title className="white">Asignar persona al Proyecto</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={formik.handleSubmit}>
                    <Form.Group className="mb-3" controlId="personEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control type="text" name="personEmail" value={personEmail} readOnly />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="projectId">
                        <Form.Label>Proyecto</Form.Label>
                        <Form.Control as="select" name="projectId" value={formik.values.projectId} onChange={(e) => formik.setFieldValue('projectId', parseInt(e.target.value, 10))}  isInvalid={formik.touched.projectId && !!formik.errors.projectId}>
                            <option value={0}>Seleccione un Proyecto</option>
                            {projects.map((project) => (
                                <option key={project.id} value={project.id}>{project.projectName}</option>
                            ))}
                        </Form.Control><br></br>
                        <Form.Label>Estado</Form.Label>
                        <Form.Control as="select" name="status" value={formik.values.status} onChange={(e) => formik.setFieldValue('status', parseInt(e.target.value, 10))}  isInvalid={formik.touched.status && !!formik.errors.status}>
                            <option value="null">Seleccione estado</option>
                            <option value={0}>Inactivo</option>
                            <option value={1}>Activo</option>
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">{formik.errors.projectId}</Form.Control.Feedback>
                    </Form.Group>
                    <div className="d-flex justify-content-end">
                        <Button type="submit" className="button-submit">Enviar</Button><br></br>
                    </div>                    
                </Form>
                <h5>Historial de proyectos asignados</h5>
                <SimpleBar style={{ maxHeight: '400px' }}>
                    <Table striped bordered hover>
                        <thead className="head_block ">
                            <tr>
                                <td className="head_t">Proyecto</td>
                                <td className="head_t">Estado</td>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedData.length > 0 ? (
                                displayedData.map((row, index) => (
                                    <tr key={index}>
                                        <td>{row.project.projectName}</td>
                                        <td>{row.status === 1 ? 'Activo' : 'Inactivo'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={2} className="text-center">No hay proyectos asignados</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </SimpleBar>
                {pagination.numberPages > 1 && (
                    <div className="custom-pagination text-center mt-3">
                        <Button variant="secondary" className="me-2" onClick={() => handlePaginationChange(pagination.page - 1)} disabled={pagination.page === 1}>
                            &lt;
                        </Button>
                        {Array.from({ length: pagination.numberPages }, (_, i) => i + 1).map(page => (
                            <Button key={page} className={`custom-pagination-item ${pagination.page === page ? 'active' : ''}`} onClick={() => handlePaginationChange(page)}>
                                {page}
                            </Button>
                        ))}
                        <Button onClick={() => handlePaginationChange(pagination.page + 1)} disabled={pagination.page === pagination.numberPages}>
                            &gt;
                        </Button>
                    </div>
                )}
            </Modal.Body>
            <ModalAlert isOpen={modalAlertOpen} onClose={() => setModalAlertOpen(false)} message={alertMessage} type={alertType} />
        </Modal>
    );
};

export default ModalProjectPerson;
